import { editRequestWebSocket } from "@/aawebsocket/websocket.gen";
import { appState } from "@/app/appState";
import { ChatArtifact, ChatDocument, ChatEvent, ChatTag, chatState } from "@/app/chatState";
import { EntryItem, useRefState } from "@/components/UI/tools"
import { useCallback, useEffect, useMemo, useState } from "react";
import { Request } from "@/components/CRUD";
import { AdminUpdateArtifactData } from "@/api/types.gen";
import { adminUpdateArtifact, chatUpdateArtifact } from "@/api/sdk.gen";
import { removeEmptyLikeFields, setHasChagedCallback, shortenLabel } from "../chatObjectEditorWindow";
import { Org, User } from "@/components/UI/svgs";
import { artifact } from "@/app/typing/artifacts";
import { calendar_event_list_item } from "@/app/typing/events";
import { document_list_item } from "@/app/typing/documents";
import { decodeSource, encodeSource, sourceOptions } from "../../artifactSource";
import { tag_list_item } from "@/app/typing/tags";
import { computeUserInputChangesOnArtifact } from "../../chatContainer";
import { getActiveArtifactKey } from "../../chatContainer";
import { applyDiffs } from "@/components/diffs";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";

import { ChatObjectType } from "@/app/chatState";
export const ArtifactEditor = observer(({ objectKey }: { objectKey?: { type: ChatObjectType; id: number } }) => {

    const activeKey = objectKey ?? chatState.selectedObjectKey!;

    const [refresh, setRefresh] = useState(0);
    const [loading, setLoading] = useState(false);

    const [id, setId] = useState<number | undefined>(undefined);
    const [revisionId, setRevisionId] = useState<number | undefined>(undefined);

    const [deleting, setDeleting] = useState(false);
    const [lock, setLock] = useRefState<boolean>(false);

    const [permissionType, setPermissionType] = useRefState<number | undefined>(undefined);
    const [isDraft, setIsDraft] = useRefState(false)
    const [edit, setEdit] = useState(false)

    const [tagSearch, setTagSearch] = useState<string>("")
    const [tagPageNumber, setTagPageNumber] = useState<number>(0)

    const [sourceSearch, setSourceSearch] = useState<string>("")
    const [sourcePageNumber, setSourcePageNumber] = useState<number>(0)

    const [revisionPage, setRevisionPage] = useState(0)
    const [maxRevPage, setMaxRevPage] = useState(0)

    const reloadTags = async () => {
        await appState.getTags({ pageNumber: tagPageNumber, search: tagSearch })
    };

    // Both source kinds feed one picker, so they page and search together.
    const reloadSources = async () => {
        await appState.getEvents({ pageNumber: sourcePageNumber, search: sourceSearch })
        await appState.getDocuments({ pageNumber: sourcePageNumber, search: sourceSearch, tagsFilter: chatState.tagIds })
    };

    useEffect(() => {

        reloadTags()
    }, [tagPageNumber, tagSearch])

    useEffect(() => {
        reloadSources()
    }, [sourcePageNumber, sourceSearch])

    const validateArtifact = (obj: any) => {
        const keys = ['id', 'variable_name', 'content', 'event_id', 'document_id', 'tag_ids', 'created_at', 'last_updated_at', 'permission_type', 'is_draft', 'user_lock']

        if (!obj || typeof obj !== 'object' || !("artifact" in obj)) {
            throw new Error("Invalid artifact object");
        }

        for (const key of keys) {
            if (!(key in obj.artifact)) {
                throw new Error(`Missing key ${key} in artifact object`);
            }
        }

        return obj as ChatArtifact;

    }

    const objState = useMemo(() => {
        return runInAction(() => {
            const key = activeKey;

            console.log("REFRESHING ARTIFACT EDITOR", key.id, chatState.chatObjects.get(key)?.id);

            if (key && key?.type == "artifact") {
                let obj: ChatArtifact | null = null;
                let revision_page = 0
                obj = key ? (chatState.chatObjects.get(key) as ChatArtifact) : null
                //console.log("REFRESHING REFS", key, obj)
                revision_page = obj?.revision_index !== undefined ? obj?.revision_index : -1
                setRevisionPage(revision_page)

                try {
                    obj = validateArtifact(obj)
                } catch (e) {
                    chatState.getObjectByKey({ type: "artifact", id: key.id })
                    return
                }

                const revisions = obj?.revisions?.slice().sort((a, b) => a.id - b.id).filter(x => x ? true : false) ?? []
                setMaxRevPage(revisions.length)

                if (!('content' in obj.artifact)) {
                    console.error("Artifact object is missing content field", obj)
                    return
                }

                if (revisions.length > 0) {
                    if (revision_page > revisions.length)
                        return

                    const maxIndex = revision_page + 1
                    const isLastRevision = maxIndex == revisions.length
                    const isRoot = revision_page == -1
                    console.log("IS LAST REVISION", isLastRevision, maxIndex, revisions.length, revision_page, revisions[revision_page])
                    setEdit(isLastRevision)
                    const curr = revisions[revision_page] ?? obj.artifact
                    const revStrings = revisions.slice(0, maxIndex).map(x => x?.content_diff) ?? []


                    const root = obj.artifact.content ?? ""

                    if (curr) {

                        setLock(obj.artifact.user_lock ? !(obj.artifact.user_lock == appState.user?.id) : false)
                        setId(obj.id)
                        setPermissionType(obj.artifact.permission_type)
                        setRevisionId(isRoot ? undefined : curr.id)
                        setIsDraft(obj.artifact.is_draft)

                        const content = applyDiffs(root, [...revStrings])
                        console.log("COMPUTED CONTENT FOR REVISION", { root, revStrings, content })
                        // A revision carries the source the agent proposed, so it wins
                        // over the artifact's current one while viewing that revision.
                        const old_obj = { content, variable_name: curr?.variable_name, tag_ids: curr?.tag_ids, event_id: curr?.event_id ?? null, document_id: curr?.document_id ?? null }
                        setHasChagedCallback(old_obj);
                        const variable_name = obj.temp_obj?.variable_name ?? old_obj?.variable_name ?? ""

                        console.log("SETTING HAS CHANGED CALLBACK FOR REVISION ARTIFACT", { ...old_obj, ...obj.temp_obj, variable_name: variable_name });
                        if (isLastRevision) {
                            return { ...old_obj, ...removeEmptyLikeFields(obj.temp_obj), variable_name: variable_name }
                        } else {
                            return { ...old_obj, variable_name: variable_name };
                        }
                    }

                }
                else {
                    if (obj) {

                        setEdit((obj.artifact.is_draft || obj.unsaved_changes) ? true : (obj?.edit ?? false))
                        setIsDraft(obj.artifact.is_draft)
                        setLock(obj.artifact.user_lock ? !(obj.artifact.user_lock == appState.user?.id) : false)
                        setId(obj.id)
                        setPermissionType(obj.artifact.permission_type)
                        setRevisionId(undefined)

                        const old_obj = { content: obj.artifact.content, variable_name: obj.artifact.variable_name, tag_ids: obj.artifact.tag_ids, event_id: obj.artifact.event_id, document_id: obj.artifact.document_id }

                        console.log("SETTING HAS CHANGED CALLBACK FOR NON REVISION ARTIFACT", old_obj)
                        setHasChagedCallback(old_obj);

                        return { ...old_obj, ...removeEmptyLikeFields(obj.temp_obj) };

                    } else {
                        setRevisionId(undefined)
                        setIsDraft(false)

                        setLock(true)
                        setId(undefined)
                        setPermissionType(undefined)

                        return { variable_name: "", content: "", tag_ids: [], event_id: null, document_id: null }

                    }
                }
            }
        })


    }, [
        activeKey,
        (chatState.chatObjects.get(activeKey) as ChatArtifact)?.artifact,
        ((chatState.chatObjects.get(activeKey) as ChatArtifact)?.artifact as artifact)?.content,
        (chatState.chatObjects.get(activeKey) as ChatArtifact)?.revision_index,
        chatState.chatObjects.get(activeKey)?.edit,
        chatState.chatObjects.get(activeKey)?.revisions?.length,
        chatState.lastExternalUpdate
    ]);

    const currentObj = {
        ...objState, ...removeEmptyLikeFields(chatState.chatObjects.get(activeKey)?.temp_obj)
    }





    const publishRevision = useCallback(async (p_is_draft: boolean | null = null) => { // is_draft argument serves to have this as a publish funcitons
        setLoading(true);
        // if we have edits, and we are on the latest revision, send those as new content diff
        const isRoot = revisionId == undefined
        const isLastRevision = revisionPage == (maxRevPage - 1)
        const result = computeUserInputChangesOnArtifact(activeKey)
        const content_diff = result?.content_diff ?? undefined
        const variable_name = currentObj?.variable_name ?? ""
        const tag_ids = isLastRevision ? currentObj?.tag_ids ?? [] : objState?.tag_ids ?? [] // only send updated tag_ids if on last revision
        const event_id = currentObj?.event_id ?? null
        const document_id = currentObj?.document_id ?? null
        // The note nests under whichever source it points at; only one can be set.
        const sourceKey = event_id
            ? { type: "event" as const, id: event_id }
            : document_id
                ? { type: "document" as const, id: document_id }
                : null


        if (!objState) {
            setLoading(false);
            return;
        }

        try {
            // Get values from refs directly

            if (id && objState.variable_name && objState.tag_ids && chatState.conversationId && isDraft.current != undefined) {
                const response: any = await chatUpdateArtifact({
                    body: {
                        id: id,
                        conversation_id: chatState.conversationId,
                        tag_ids: tag_ids,
                        // At the root of open revisions, saving discards them and keeps the root content.
                        content: !revisionId ? (maxRevPage > 0 ? objState.content : currentObj.content) : undefined,
                        event_id: event_id,
                        document_id: document_id,

                        content_diff: content_diff,
                        variable_name: variable_name,
                        is_draft: p_is_draft == null ? isDraft.current : p_is_draft,
                        revision_id: revisionId ?? null
                    },
                });

                if (response.data) {
                    runInAction(() => {
                        if (isRoot || isLastRevision) {

                            chatState.chatObjects.update(activeKey ? [{ key: activeKey, value: { unsaved_changes: false, artifact: { variable_name: variable_name, content: currentObj.content, tag_ids: tag_ids } } }] : [])
                            chatState.setObjectEditorTemp(null, activeKey)
                        }

                        chatState.chatObjects.update(activeKey ? [{ key: activeKey, value: { revision_index: -1 } }] : [])
                        if (id)
                            chatState.flushRevisions({ type: "artifact", id: id }, revisionId) // TODO flush to revision id, remove chat_refresh to avoid dublicating revisions
                        appState.getArtifacts({ pageNumber: 0, search: "" });
                    })

                    // Await both fetches so chatObjects is fully up-to-date before re-selecting.
                    // Without this, looking up artifact.parent immediately after publish returns
                    // undefined when the artifact just gained a source for the first time,
                    // causing the tab to vanish once getObjectByKey resolves and makes it a child node.
                    if (activeKey) await chatState.getObjectByKey({ type: "artifact", id: activeKey.id });
                    if (sourceKey) await chatState.getObjectByKey(sourceKey);

                    runInAction(() => {
                        appState.chat_refresh = appState.chat_refresh + 1 // TODO FIX THIS ABOMINAION
                        // Use the source from scope rather than stale chatObjects.parent lookup
                        if (sourceKey) {
                            const parentKey = sourceKey;
                            const treeNodes = chatState.chatObjects.entries([parentKey as any]);
                            const children = treeNodes.length > 0 ? treeNodes[0].children : [];
                            const childIndex = children.findIndex((c: any) => c.key.type === "artifact" && c.key.id === id);
                            chatState.chatObjects.update([{ key: parentKey, value: { child_index: childIndex >= 0 ? childIndex : 0 } }]);
                            chatState.setSelectedObjectKey(parentKey as any, true);
                        } else {
                            chatState.setSelectedObjectKey({ type: 'artifact', id: id }, true)
                        }
                    })
                    setEdit(false)

                    return true;
                } else {
                    if (response.error) {
                        console.error("Error occurred:", response);
                        appState.setModal({ title: appState.t("common.error"), body: appState.t("objects.errorUpdateArtifact") + " - " + response.error.detail });
                        setRefresh((p) => p + 1);
                    }
                }
            } else {
                console.error("Missing requried field!", "id", id, "variable_name", objState?.variable_name, "content len", objState?.content?.length, "isDraft", isDraft.current != undefined);
            }
        } finally {
            setLoading(false);

        }
    }, [id, currentObj.variable_name, currentObj.tag_ids, currentObj.content, currentObj.event_id, currentObj.document_id, revisionId, maxRevPage]); // all values sent should go here


    const updateArtifact = Request<AdminUpdateArtifactData, any>(
        adminUpdateArtifact,
        () => ({
            body: {
                id: activeKey?.id!,
                tag_ids: currentObj?.tag_ids ?? [],
                variable_name: currentObj?.variable_name ?? "",
                content: currentObj?.content ?? "",
                event_id: currentObj?.event_id ?? null,
                document_id: currentObj?.document_id ?? null,
            },
        }),
        setLoading,
        (data) => {
            runInAction(() => {
                // Artifact needs to be hard set, to trigger refreshref, and create isChanged handler
                chatState.chatObjects.update(activeKey ? [{ key: activeKey, value: { unsaved_changes: false, artifact: { variable_name: objState?.variable_name, content: objState?.content, tag_ids: objState?.tag_ids, event_id: objState?.event_id, document_id: objState?.document_id } } }] : [])
                chatState.setObjectEditorTemp(null, activeKey)

                if (activeKey) {
                    chatState.flushRevisions({ type: "artifact", id: activeKey?.id })
                    chatState.getObjectByKey({ type: "artifact", id: activeKey?.id })
                    if (currentObj?.event_id)
                        chatState.getObjectByKey({ type: "event", id: currentObj?.event_id })
                    else if (currentObj?.document_id)
                        chatState.getObjectByKey({ type: "document", id: currentObj?.document_id })

                }
                appState.getArtifacts({ pageNumber: 0, search: "" });
            })

        },
        (error) => {
            setRefresh(p => p + 1)
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("objects.errorUpdateArtifact"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            })
        },
        () => activeKey?.id != undefined && activeKey?.id > 0,
    )


    useEffect(() => {
        currentObj.tag_ids?.forEach(tag_id => {
            if (!appState.tags?.find(t => t.id === tag_id) && !chatState.chatObjects.get({ type: "tag", id: tag_id })) {
                chatState.getObjectByKey({ type: "tag", id: tag_id });
            }
        });
    }, [currentObj.tag_ids]);

    useEffect(() => {
        if (currentObj.event_id && !appState.events?.find(e => e.id === currentObj.event_id) && !chatState.chatObjects.get({ type: "event", id: currentObj.event_id })) {
            chatState.getObjectByKey({ type: "event", id: currentObj.event_id });
        }
        if (currentObj.document_id && !appState.documents?.find(d => d.id === currentObj.document_id) && !chatState.chatObjects.get({ type: "document", id: currentObj.document_id })) {
            chatState.getObjectByKey({ type: "document", id: currentObj.document_id });
        }
    }, [currentObj.event_id, currentObj.document_id]);

    const dateOptions: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'numeric', day: 'numeric' };

    if (id && id == activeKey?.id) {
        return (
            <div className="flex flex-col h-full min-h-0 w-full">
            <div className="flex-1 min-h-0">
            <EntryItem
                isRevisionRoot={(revisionId === undefined) && (maxRevPage > 0)}
                forcedEdit={(isDraft.current || maxRevPage > 0) ? true : false}
                unsavedChanges={(chatState.chatObjects.get(activeKey!) as ChatArtifact)?.unsaved_changes ?? false}
                lock={lock.current}
                setEdit={async (val: boolean) => {
                    if (val)
                        await editRequestWebSocket(activeKey?.id ?? -1, (res) => {
                            chatState.chatObjects.update(activeKey ? [{ key: activeKey, value: { edit: res != null } }] : [])
                        }, "artifact")
                    else
                        chatState.chatObjects.update(activeKey ? [{ key: activeKey, value: { edit: false } }] : [])
                }}
                edit={edit}
                name={isDraft.current ? appState.t("chat.tool.draftArtifact") : appState.t("objects.list.artifact")}
                created_at={new Date((chatState.chatObjects.get(activeKey!) as ChatArtifact)?.artifact.created_at ?? "").toLocaleDateString(undefined, dateOptions)}
                loading={loading}
                deleting={deleting}
                readonly={((![4, 5, 6, 7].includes(appState.user?.active_organisation.permission_type ?? 0) || ![4, 5, 6, 7].includes(permissionType.current ?? 0)))}
                chatEditorWindowClose={() => {
                    chatState.removeSelectedObjectKey()
                }
                }
                updateEntry={((maxRevPage > 0 || chatState.conversationId) ? () => publishRevision(false) : updateArtifact)}
                refreshCount={refresh}
                idParams={{
                    singular: { get: activeKey?.id, set: () => { } },
                    plural: { get: [], set: () => { } },
                }}
                refColumns={[
                    {
                        type: "richtext",
                        label: appState.t("objects.content"),
                        validator: (value: string | undefined) => (value ? true : false),
                        value: currentObj?.content,
                        onChange: (value) => chatState.setObjectEditorTemp({ content: value }, objectKey ? activeKey : undefined),
                        artifactsComplete: true,
                    },
                ]}
                nameColumn={{
                    type: "text",
                    value: currentObj?.variable_name ?? "",
                    onChange: (value) => chatState.setObjectEditorTemp({ variable_name: value }, objectKey ? activeKey : undefined),
                    validator: (val: string) => !/["'${}`,]/.test(val),
                    label: appState.t("objects.variableName"),
                }}
                stateColumns={[{
                    label: "#",
                    type: "multiple",
                    onChange: (value: number[]) => { chatState.setObjectEditorTemp({ tag_ids: value }, objectKey ? activeKey : undefined) },
                    search: { get: tagSearch, set: setTagSearch },
                    pageNumber: { get: tagPageNumber, set: setTagPageNumber },
                    value: currentObj?.tag_ids ?? [],
                    items: [...(appState.tags ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "tag" && (x.value as ChatTag).tag && !(appState.tags ?? []).find(t => t.id === x.value.id)).map(x => (x.value as ChatTag).tag as any as tag_list_item)].filter((x) => [4, 5, 6, 7].includes(x.effective_permission_type)).map((x) => ({
                        id: x.id,
                        name: x.name,
                        children: (
                            <div className="flex flex-row gap-[0.5rem] items-center">
                                <>{shortenLabel(x.name)}</>
                                {x.scope == 'me' ? <User size={16} /> : <Org size={16} />}
                            </div>
                        ),
                        defaultValue: currentObj?.tag_ids?.includes(x.id)
                    })),
                    validator: (val: number[]) => val?.length > 0,
                }, {
                    label: appState.t("objects.source"),
                    type: "single",
                    onChange: (value: number | undefined) => { chatState.setObjectEditorTemp(decodeSource(value) as any, objectKey ? activeKey : undefined) },
                    search: { get: sourceSearch, set: setSourceSearch },
                    pageNumber: { get: sourcePageNumber, set: setSourcePageNumber },
                    value: encodeSource(currentObj),
                    items: sourceOptions(
                        // Sources already open in the chat may not be in the paged list.
                        [...(appState.events ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "event" && (x.value as ChatEvent).event && !(appState.events ?? []).find(e => e.id === x.value.id)).map(x => (x.value as ChatEvent).event as any as calendar_event_list_item)],
                        [...(appState.documents ?? []), ...chatState.chatObjects.entries().filter(x => x.value.type === "document" && (x.value as ChatDocument).document && !(appState.documents ?? []).find(d => d.id === x.value.id)).map(x => (x.value as ChatDocument).document as any as document_list_item)],
                        chatState.tagIds,
                    ).map((x) => ({
                        id: x.id,
                        name: x.name,
                        children: (
                            <div className="flex flex-row gap-[0.5rem] items-center">
                                <span className="chip-prefix">{appState.t(x.type === "document" ? "objects.list.document" : "objects.list.event")}</span>
                                <>{shortenLabel(x.name)}</>
                            </div>
                        ),
                    })),
                    validator: () => true,
                }]}
            />
            </div>
            </div>
        )
    }
})